/**
 * Client Entry Point — Browser Hydration
 *
 * Hydrates the server-rendered HTML with React, attaching event handlers
 * and enabling client-side navigation via React Router.
 *
 * This is the esbuild entry point for the client bundle.
 * All route-level lazy() imports become code-split chunks.
 *
 * The client route tree (routes.client.gen.tsx) contains ONLY UI components —
 * no loaders, no Connect-RPC, no protobuf, no process.env. Data fetching
 * for client-side navigation is handled by the dataStrategy, which fetches
 * from the /_data BFF endpoint on the Hono server.
 */
import '../styles.gen.css';

import { BFF_ROUTE_URL_HEADER } from '@/shared/lib/bff-headers';
import { decodeDataRouteErrorFrame, isDataRouteErrorStatus } from '@/shared/lib/data-route-error';
import { mergeDeliveredFlags, type EvaluatedWebFlags } from '@letsgethome/lib/flags/evaluate';
import { parseEnvSlug } from '@letsgethome/lib/flags/registry_public_gen';
import { plaintextString } from '@letsgethome/types/branded';
import { StrictMode } from 'react';
import { hydrateRoot } from 'react-dom/client';
import { RouterProvider, type HydrationState } from 'react-router';

import { AppErrorBoundary } from '../shared/components/error-boundary';
import { clientLogger } from '../shared/lib/client-logger';
import { installMapsAuthFailureHandler } from '../shared/lib/maps-health';
import { reportHydration } from '../shared/lib/metrics/hydration';
import type { RuntimeConfig } from '../shared/providers/ConfigProvider';
import { createHydratedRouter } from './create-hydrated-router';
import { HtmlDocument, type ShellManifest } from './HtmlDocument';
import { localizedRoutes } from './routes.client.gen';
import { ShellProviders } from './ShellProviders';

declare global {
  interface Window {
    // Optional: SSR injects these, but they may be absent (e.g. a hydration
    // before the bootstrap script ran) — the `?? fallback` reads stay honest.
    __LOCALE__?: string;
    __MESSAGES__?: Record<string, string>;
    __CONFIG__: RuntimeConfig;
    __FLAGS__?: EvaluatedWebFlags;
    __FLAGS_ENV__?: string;
    __TRACE_ID__?: string;
    __staticRouterHydrationData?: HydrationState;
    // Build-derived head inputs the server serialized so the client can render
    // a `<head>` identical to the server's (see server/bootstrap.ts).
    __SHELL__?: ShellManifest;
  }
}

// Global browser-error capture. Registered before hydration so an uncaught
// exception or unhandled rejection from ANY script (incl. third-party like
// Google Maps) reaches Loki via the existing clientLogger beacon — previously
// there was no window.onerror/unhandledrejection handler at all, so those were
// lost. gm_authFailure is installed here too, the earliest point, so Google's
// auth-failure callback has a handler before the Maps SDK loads.
window.addEventListener('error', (event) => {
  clientLogger.error(
    {
      component: 'window',
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error: event.error,
    },
    'Uncaught error'
  );
});
window.addEventListener('unhandledrejection', (event) => {
  clientLogger.error(
    { component: 'window', reason: String(event.reason) },
    'Unhandled promise rejection'
  );
});
installMapsAuthFailureHandler();

const locale = window.__LOCALE__ ?? 'zh-HK';
const messages = window.__MESSAGES__ ?? {};
const runtimeConfig = window.__CONFIG__;
// PER-KEY merge onto the compiled floor — the SAME floor env the server
// rendered with (serialized as __FLAGS_ENV__ from server/flags.ts
// floorEnv), so dev hydration floors dev and deployed hydration floors
// prod; a missing/unknown value degrades to the conservative prod floor.
// Same per-key skew story: cached HTML from an older server that lacks a
// newly-registered key degrades that key alone, never an `undefined`
// typed as boolean.
const flags: EvaluatedWebFlags = mergeDeliveredFlags(
  parseEnvSlug(window.__FLAGS_ENV__ ?? '') ?? 'prod',
  new Map(Object.entries(window.__FLAGS__ ?? {}))
);

const ssrLoaderData = { ...window.__staticRouterHydrationData?.loaderData };

const router = await createHydratedRouter(localizedRoutes, {
  hydrationData: window.__staticRouterHydrationData,
  async dataStrategy({ matches, request }) {
    // React Router's canonical data-strategy shape: only resolve matches whose
    // handler should run (revalidation/freshness). Matches filtered out keep
    // their existing data rather than being reloaded. `shouldCallHandler` is a
    // METHOD — it must be *called*; the previous `!match.shouldCallHandler`
    // tested the (always-truthy) function reference and never short-circuited.
    const matchesToLoad = matches.filter((match) => match.shouldCallHandler());
    return Object.fromEntries(
      await Promise.all(
        // `as const` makes each entry a `[string, DataStrategyResult]` tuple so
        // `Object.fromEntries` infers `Record<string, DataStrategyResult>`
        // instead of collapsing to `any` (the loose array-overload).
        matchesToLoad.map(
          async (match) =>
            [
              match.route.id,
              await match.resolve(async () => {
                if (ssrLoaderData[match.route.id] !== undefined) {
                  const data: unknown = ssrLoaderData[match.route.id];
                  Reflect.deleteProperty(ssrLoaderData, match.route.id);
                  return data;
                }

                // Only the route URL is sent: the BFF derives the locale from
                // it (the single source). A separate X-Locale header would be a
                // second, divergable locale source — exactly the bug class this
                // closes (ADR 0142).
                const res = await fetch(`/_data/${match.route.id}`, {
                  headers: {
                    [BFF_ROUTE_URL_HEADER]: request.url,
                  },
                });
                // A refusal is RETHROWN AS A `Response`, never as an `Error`.
                // React Router converts a `Response` thrown inside a data
                // strategy into an `ErrorResponse` carrying its status, which
                // is the value `isRouteErrorResponse` matches and the route's
                // `ErrorBoundary` renders a 404 page from. `new Error(...)` —
                // what this used to throw — is an unrecognised failure, so a
                // deleted or unpublished entity rendered the generic crash
                // boundary on a clicked link while the identical URL typed into
                // the address bar rendered the proper not-found page.
                if (!res.ok) {
                  const frame = decodeDataRouteErrorFrame(await res.json().catch(() => undefined));
                  // `frame` is absent for any non-2xx this app did not mint (a
                  // proxy's HTML error page, an aborted body). The raw status is
                  // then the honest floor — still a `Response`, so the failure
                  // is still an HTTP error and not a mystery `TypeError`.
                  //
                  // ⚠ The floor is GUARDED, because the constructor two lines
                  // below is not total over it: `new Response('null', {status})`
                  // THROWS for a null-body status (304, 101, 103 — all `!ok`, so
                  // all reachable here), and that throw lands in the generic
                  // crash boundary this whole change exists to remove, from the
                  // one code path whose job is to render a clean error page. A
                  // status that cannot spell a refusal degrades to 500 through
                  // the SAME predicate the frame's own constructor uses.
                  const status =
                    frame?.status ?? (isDataRouteErrorStatus(res.status) ? res.status : 500);
                  // eslint-disable-next-line @typescript-eslint/only-throw-error -- a `Response` is REQUIRED here, not an Error: React Router converts a thrown Response inside a data strategy into an ErrorResponse carrying its status (the value `isRouteErrorResponse` matches and the route's ErrorBoundary renders a 404 from). Throwing an Error is what produced the generic crash boundary this change removes.
                  throw new globalThis.Response(JSON.stringify(frame?.data ?? null), {
                    status,
                    // `frame.statusText` is a legal HTTP reason phrase by
                    // construction (the frame's constructor reduces it to one),
                    // and `res.statusText` came off the wire through fetch's own
                    // parser — so neither can make this constructor throw.
                    statusText: frame?.statusText ?? res.statusText,
                    headers: { 'Content-Type': 'application/json' },
                  });
                }
                return res.json();
              }),
            ] as const
        )
      )
    );
  },
});

const hydrationStart = performance.now();

// Last-resort hydration-failure copy. Intentionally hardcoded (not via i18n):
// this renders when the React tree — including the IntlProvider — failed to
// mount, so no translation context is available. Held as a constant so it sits
// outside JSX-text position (no i18next/no-literal-string, no disable needed).
const HYDRATION_ERROR_MESSAGE = plaintextString('Something went wrong.');

// The shell manifest the server serialized (css + modulepreloads + locale) so
// the client renders a `<head>` identical to the server's. Falls back to a
// bare shell if absent (a pre-bootstrap hydration) — the ?? reads stay honest.
const shell: ShellManifest = window.__SHELL__ ?? { locale, cssFiles: [], preloadChunks: [] };

// Hydrate the WHOLE document with the SAME `HtmlDocument`/`ShellProviders` the
// server rendered (the SSOT) — React owns <head>, so SeoHead/Image metadata
// hoists symmetrically and cannot duplicate. `AppErrorBoundary` + `StrictMode`
// are DOM-transparent, so they don't perturb the hydrated structure.
hydrateRoot(
  document,
  <StrictMode>
    <HtmlDocument
      locale={shell.locale}
      cssFiles={shell.cssFiles}
      preloadChunks={shell.preloadChunks}
    >
      <ShellProviders locale={locale} messages={messages} config={runtimeConfig} flags={flags}>
        <AppErrorBoundary fallback={<p>{HYDRATION_ERROR_MESSAGE}</p>}>
          <RouterProvider router={router} />
        </AppErrorBoundary>
      </ShellProviders>
    </HtmlDocument>
  </StrictMode>
);

reportHydration(hydrationStart);
void import('../shared/lib/metrics/web-vitals').then((m) => m.initWebVitals());
